import React, { FC, useContext, useMemo, useState } from 'react'
import { Trans, t } from '@lingui/macro'
import classnames from 'classnames'

import { Modal } from '../../../components/common/modal/Modal'
import { Checkbox } from '../../../components/common/form/checkbox/Checkbox'
import { ManageCampaignContext, SingleCampaign } from '../ManageCampaignPage'

import { translateActionType } from '../campaign-overview/create-campaign-action-modal/CreateCampaignActionModal'
import { SelectedActions } from './CampaignLinkReport'
import IconLink from '../../../components/common/icon/IconLink'
import { ButtonLight, ButtonPrimary } from 'front-end/src/components'
import { stripHTMLTags } from '../../../../../shared/HTML'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

const FilterCampaignActionsModal: FC<{
  onChange: (selectedActions: SelectedActions) => void
  selectedActions: SelectedActions
  hideActionFilterModal: () => void
}> = (props) => {
  const { campaign } = useContext(ManageCampaignContext)

  const flatCampaignActions = useMemo<Array<ICampaignActionForModal>>(
    () => getFlatListOfActions(campaign),
    [campaign]
  )

  const [selectedCampaignActions, setSelectedCampaignActions] =
    useState<SelectedActions>(props.selectedActions)

  const handleSubmit = () => {
    props.onChange(selectedCampaignActions)
    props.hideActionFilterModal()
  }

  const onCampaignActionClicked = (campaignAction: ICampaignActionForModal) => {
    setSelectedCampaignActions(
      toggleSelectedCampaignAction(campaignAction, selectedCampaignActions)
    )
  }

  return (
    <Modal
      show
      onHide={() => props.hideActionFilterModal()}
      controls={
        <>
          <ButtonLight onClick={() => props.hideActionFilterModal()}>
            <Trans>Cancel</Trans>
          </ButtonLight>
          <ButtonPrimary onClick={handleSubmit}>
            <Trans>Set filter</Trans>
          </ButtonPrimary>
        </>
      }
      header={t`Campaign actions`}
      subHeader={t`Filter to include only link reports by members of 1 or more campaign actions:`}
      className="CampaignReportsFiltersModal"
    >
      <div className="List">
        {flatCampaignActions.map((campaignAction) => (
          <label
            key={campaignAction.id}
            className={classnames('List__Row', {
              active: isCampaignActionSelected(
                campaignAction,
                selectedCampaignActions
              )
            })}
          >
            <Checkbox
              isChecked={isCampaignActionSelected(
                campaignAction,
                selectedCampaignActions
              )}
              onChange={() => {
                onCampaignActionClicked(campaignAction)
              }}
            />
            <div className="List__Row__actionName">
              {campaignAction.actionName}
            </div>
            <div className="List__Row__actionType">
              {translateActionType(campaignAction.actionType)}
            </div>
            <div className="List__Row__clickCount">
              <IconLink /> {campaignAction.linksCount}
            </div>
          </label>
        ))}
      </div>
    </Modal>
  )
}

export default FilterCampaignActionsModal

export interface ICampaignActionForModal {
  id: number
  actionType: CampaignActionTypeEnum
  actionName: string
  label: string
  linksCount: number
}

export const getFlatListOfActions = (
  campaign: SingleCampaign
): Array<ICampaignActionForModal> => {
  const flatActions: Array<ICampaignActionForModal> = []
  campaign.emailActions.forEach((email) => {
    flatActions.push({
      id: email.id,
      actionType: CampaignActionTypeEnum.EMAIL,
      label: t`Send email ${email.subject}`,
      actionName: email.subject,
      linksCount: email.trackedLinksCount
    })
  })

  campaign.pathwayShareActions.forEach((pathwayShareAction) => {
    const pathwaysCount = pathwayShareAction.actionPathways.length
    const label =
      pathwaysCount > 1
        ? t`Share ${pathwaysCount} pathways`
        : t`Share ${pathwayShareAction.actionPathways[0].pathway.title} pathway`

    const actionName =
      pathwaysCount > 1
        ? t`${pathwaysCount} pathways`
        : pathwayShareAction.actionPathways[0].pathway.title

    flatActions.push({
      id: pathwayShareAction.id,
      actionType: CampaignActionTypeEnum.PATHWAY_SHARE_ACTION,
      label,
      actionName,
      linksCount: pathwayShareAction.trackedLinksCount
    })
  })

  campaign.certificationShareActions.forEach((certificationShareAction) => {
    const certificationsCount =
      certificationShareAction.actionCertifications.length
    const label =
      certificationsCount > 1
        ? t`Share ${certificationsCount} certifications`
        : t`Share ${certificationShareAction.actionCertifications[0].certification.name} certification`

    const actionName =
      certificationsCount > 1
        ? t`${certificationsCount} certifications`
        : certificationShareAction.actionCertifications[0].certification.name

    flatActions.push({
      id: certificationShareAction.id,
      actionType: CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION,
      label,
      actionName,
      linksCount: certificationShareAction.trackedLinksCount
    })
  })

  campaign.topicShareActions.forEach((topicShareAction) => {
    const topicsCount = topicShareAction.actionTopics.length
    const label =
      topicsCount > 1
        ? t`Share ${topicsCount} resources`
        : t`Share ${topicShareAction.actionTopics[0].topic.currentTitle} resource`

    const actionName =
      topicsCount > 1
        ? t`${topicsCount} resources`
        : topicShareAction.actionTopics[0].topic.currentTitle

    flatActions.push({
      id: topicShareAction.id,
      actionType: CampaignActionTypeEnum.TOPIC_SHARE_ACTION,
      label,
      actionName,
      linksCount: topicShareAction.trackedLinksCount
    })
  })

  campaign.surveyShareActions.forEach((surveyShareAction) => {
    const surveysCount = surveyShareAction.actionSurveys.length
    const label =
      surveysCount > 1
        ? t`Share ${surveysCount} surveys`
        : t`Share ${surveyShareAction.actionSurveys[0].survey.title} survey`

    const actionName =
      surveysCount > 1
        ? t`${surveysCount} surveys`
        : surveyShareAction.actionSurveys[0].survey.title

    flatActions.push({
      id: surveyShareAction.id,
      actionType: CampaignActionTypeEnum.SURVEY_SHARE_ACTION,
      label,
      actionName,
      linksCount: surveyShareAction.trackedLinksCount
    })
  })

  campaign.messageActions.forEach((messageAction) => {
    // Strip becase for example MSTeams is in HTML
    const label = stripHTMLTags(messageAction.text || '').replace(
      /(.{25})..+/,
      '$1…'
    )

    flatActions.push({
      id: messageAction.id,
      actionType: CampaignActionTypeEnum.MESSAGE,
      label: t`Send message: ${label}`,
      actionName: label,
      linksCount: messageAction.trackedLinksCount
    })
  })

  return flatActions
}

const isCampaignActionSelected = (
  campaignAction: ICampaignActionForModal,
  selectedCampaignActions: SelectedActions
): boolean => {
  return !![...selectedCampaignActions].find(
    (selectedCampaignAction) =>
      selectedCampaignAction.type === campaignAction.actionType &&
      selectedCampaignAction.id === campaignAction.id
  )
}

const toggleSelectedCampaignAction = (
  campaignAction: ICampaignActionForModal,
  selectedActions: SelectedActions
) => {
  const isSelected = selectedActions.find((selectedAction) => {
    return (
      selectedAction.id === campaignAction.id &&
      selectedAction.type === campaignAction.actionType
    )
  })

  if (isSelected) {
    return selectedActions.filter((selectedAction) => {
      return (
        selectedAction.id !== campaignAction.id ||
        selectedAction.type !== campaignAction.actionType
      )
    })
  } else {
    return [
      ...selectedActions,
      {
        id: campaignAction.id,
        type: campaignAction.actionType
      }
    ]
  }
}
